/**
 * Lift2Fit — Manage coach · Coach tab.
 *
 * The relationship half of the old profile panel, moved here whole: the linked
 * coach (who, since when, what is shared, revoke with its confirmation), the
 * connect-by-invite flow with its copyable single-use code, and pending invites
 * in both directions. Reuses `coach.tsx`'s controls rather than a second
 * implementation.
 *
 * The injury flags live here too — they are "what my coach should know", so
 * they belong beside the sharing controls, not in the account settings.
 */
import { useState } from 'react';
import { api } from '@/lib/api';
import { cn, formatDate, timeAgo } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { Button } from '@/components/ui';
import { ShieldIcon } from '@/lib/icons';
import { LiveMessage, Panel } from '../components';
import { ConnectCoachForm, PendingInvites, SharedFieldsControl, type CoachingState } from '../coach';
import { INJURY_FLAGS_ALL } from '../demoData';

export function CoachTab({ coaching }: { coaching: CoachingState }) {
  const state = coaching;
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  const revoke = async () => {
    setBusy(true);
    setTone('info');
    setMessage('Revoking…');
    const result = await api.revokeCoachLink();
    setBusy(false);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'The link could not be revoked.');
      return;
    }
    setConfirmingRevoke(false);
    setTone('success');
    setMessage('Coach unlinked. Their access ended on the next request; they can no longer read or prescribe.');
    state.reload();
  };

  const link = state.link;
  const active = link?.status === 'active' && link.coach !== null;

  return (
    <div className="flex flex-col gap-4" data-testid="my-coach-coach-tab">
      <Panel
        title="Your coach"
        subtitle="A human coach or gym — at most one, sharing only the fields you tick."
        actions={
          active ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-muted">Linked</span>
              <button
                type="button"
                data-testid="coach-revoke"
                onClick={() => {
                  setConfirmingRevoke(true);
                  setMessage(null);
                }}
                className="inline-flex h-9 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-danger hover:border-danger"
              >
                Revoke
              </button>
            </span>
          ) : null
        }
      >
        {/* Only the FIRST load blanks the panel: a reload after a save must never
            unmount the invite result or the confirmation the user just earned. */}
        {state.loading && !state.loaded ? (
          <p className="text-[13px] text-ink-muted" data-testid="coach-loading" aria-live="polite">
            Loading your coach connection…
          </p>
        ) : state.error ? (
          <LiveMessage message={state.error} tone="danger" />
        ) : active && link ? (
          <div className="flex flex-col gap-4" data-testid="coach-linked">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-1">
                <p className="flex items-center gap-2 text-[16px] font-bold text-ink">
                  <ShieldIcon className="text-accent" />
                  {link.coach?.displayName ?? 'Your coach'}
                </p>
                <p className="text-[13px] text-ink-muted">
                  {link.coach?.email || 'no email on file'} · linked{' '}
                  {link.linkedAt ? formatDate(link.linkedAt) : 'recently'} (
                  {link.linkedAt ? timeAgo(link.linkedAt) : 'just now'})
                </p>
                <p className="text-[12px] text-ink-muted">
                  {link.origin === 'coach_invited' ? 'They invited you.' : 'You invited them.'}
                </p>
              </div>
            </div>

            <SharedFieldsControl
              sharedFields={link.sharedFields}
              onSaved={() => {
                // The control already announced the change; the parent just
                // keeps the link snapshot in step on the next reload.
                state.reload();
              }}
            />

            {confirmingRevoke ? (
              <div
                className="flex flex-col gap-2 border border-danger bg-page-subtle p-3"
                data-testid="coach-revoke-confirm"
              >
                <p className="text-[13px] font-bold text-ink">
                  Revoke {link.coach?.displayName ?? 'your coach'}?
                </p>
                <p className="text-[12px] text-ink-muted">
                  This stops immediately — on their very next request they can no longer read any shared field, see your
                  sessions, or change your programme. The link is closed, not paused; reconnecting means a fresh invite.
                  Your own log is untouched.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    data-testid="coach-revoke-confirm-button"
                    onClick={() => void revoke()}
                  >
                    {busy ? 'Revoking…' : 'Yes, revoke access'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingRevoke(false)}>
                    Keep the link
                  </Button>
                </div>
              </div>
            ) : null}

            {/* One coach at a time. The rule is enforced by the API; offering the
                attempt here means the refusal is explained, never hidden. */}
            <details className="border-t border-hairline pt-3" data-testid="coach-second-invite">
              <summary className="cursor-pointer text-[12px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-accent">
                Invite a different coach
              </summary>
              <p className="mt-2 text-[12px] text-ink-muted">
                You already have a coach, so a second invite is refused until you revoke this link.
              </p>
              <div className="mt-2">
                <ConnectCoachForm triggerLabel="Try to invite another" onInvited={state.reload} />
              </div>
            </details>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-[13px] text-ink-muted" data-testid="coach-unlinked">
              No human coach linked. Invite yours and they can read the fields you share and prescribe your programme.
            </p>
            <ConnectCoachForm onInvited={state.reload} />
          </div>
        )}

        {!state.error && (!state.loading || state.loaded) ? (
          <PendingInvites
            invites={state.invites}
            onChanged={state.reload}
            onNotice={(next, nextTone) => {
              setMessage(next);
              setTone(nextTone);
            }}
          />
        ) : null}
        <LiveMessage message={message} tone={tone} />
      </Panel>

      <InjuryFlagsPanel />
    </div>
  );
}

/**
 * The lifter's injury flags. Saved to the account (`PATCH`-backed session
 * store), and deliberately placed with the coach controls: this is the list
 * "what my coach should know".
 */
export function InjuryFlagsPanel() {
  const session = useSession();
  const user = session.user;
  const [flags, setFlags] = useState<string[]>(user?.injuryFlags ?? []);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  const toggle = (flag: string) => {
    setFlags((previous) =>
      previous.includes(flag) ? previous.filter((value) => value !== flag) : [...previous, flag],
    );
    setMessage(null);
  };

  const save = () => {
    session.updateUser({ injuryFlags: flags });
    setTone('success');
    setMessage(
      flags.length
        ? `Saved ${flags.length} injury flag(s). New plans account for them, and a coach you share pain with can see them.`
        : 'Cleared your injury flags. New plans will assume no current injury.',
    );
  };

  return (
    <Panel
      testId="my-coach-injury-flags"
      title="Injury flags"
      subtitle="Pain at 4/10 or above always changes the plan."
      actions={
        // Demoted from the orange primary (0028 IA): the page-level primary is
        // now the single Chat CTA in the header, so orange stays the one primary
        // action per view. This is an in-panel form save, not the page's CTA.
        <Button variant="secondary" size="sm" data-testid="my-coach-injury-save" onClick={save}>
          Save flags
        </Button>
      }
    >
      <div className="flex flex-wrap gap-2" role="group" aria-label="Injury flags">
        {INJURY_FLAGS_ALL.map((flag) => {
          const active = flags.includes(flag);
          return (
            <button
              key={flag}
              type="button"
              aria-pressed={active}
              data-testid={`my-coach-injury-flag-${flag}`}
              onClick={() => toggle(flag)}
              className={cn(
                'h-10 border px-3 text-[11px] font-bold uppercase tracking-[0.06em]',
                active ? 'border-danger text-danger' : 'border-hairline bg-surface text-ink hover:border-accent',
              )}
            >
              {flag.replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
      <p className="flex items-center gap-2 text-[12px] text-ink-muted">
        <ShieldIcon className="text-[14px] text-accent" />
        These are shared with your coach only if you allow the pain field. They always shape the plans you generate.
      </p>
      <LiveMessage message={message} tone={tone} />
    </Panel>
  );
}
